import { getAPIRoot, getCachedAccessToken } from "./api";

const buildAuthHeaders = () => {
  const token = getCachedAccessToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// =============================================
// PHOTO UPLOAD SERVICE WITH MANUAL REVIEW
// =============================================

/**
 * Upload photo to Supabase storage with automatic review queue
 */
export async function uploadPhoto(file, userId, photoType = "profile") {
  try {
    // Validate file
    const validationResult = await validatePhotoFile(file);
    if (!validationResult.isValid) {
      return {
        data: null,
        error: new Error(validationResult.error || "Invalid image file"),
      };
    }

    // Profile photos should feel consistent in a circular UI.
    // Center-crop them into a square before upload; keep other flows as-is.
    const preprocessed =
      photoType === "profile"
        ? await cropImageToSquare(file, 1024, 0.86)
        : file;

    // Downscale client-side before upload.
    const processed = await resizeImageMax(preprocessed, 1024, 0.85);
    const uploadFile = processed || file;

    const outExt = uploadFile.type.includes("webp")
      ? "webp"
      : uploadFile.type.includes("png")
        ? "png"
        : "jpg";
    const fileName = `${userId}_${photoType}_${Date.now()}.${outExt}`;
    const API_BASE = getAPIRoot();

    const form = new FormData();
    form.append("file", uploadFile, fileName);
    form.append("photo_type", photoType);

    const uploadResponse = await fetch(`${API_BASE}/upload/image`, {
      method: "POST",
      credentials: "include",
      headers: buildAuthHeaders(userId),
      body: form,
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text().catch(() => "");
      let message = errorText || "Upload failed";
      try {
        const parsed = errorText ? JSON.parse(errorText) : null;
        message = parsed?.error || parsed?.detail || parsed?.message || message;
      } catch (_) {}
      throw new Error(message);
    }

    const uploadResult = await uploadResponse.json().catch(() => ({}));
    const publicUrl = uploadResult?.image_url || uploadResult?.url || null;

    if (!publicUrl) {
      throw new Error("Upload succeeded but no image URL was returned");
    }

    const reviewData = await addPhotoToReviewQueue(
      userId,
      publicUrl,
      photoType,
      fileName,
    );

    return {
      data: {
        url: publicUrl,
        fileName: fileName,
        filePath: uploadResult?.file_path || null,
        reviewId: reviewData.data?.id,
        status: "pending_review",
        message:
          uploadResult?.message ||
          "Photo uploaded successfully! It will be reviewed within 24 hours.",
      },
      error: null,
    };
  } catch (error) {
    console.error("Error uploading photo:", error);
    return {
      data: null,
      error:
        error instanceof Error
          ? error
          : new Error(String(error || "Upload failed")),
    };
  }
}

/**
 * Add photo to manual review queue
 */
async function addPhotoToReviewQueue(userId, photoUrl, photoType, fileName) {
  try {
    const API_BASE = getAPIRoot();
    const response = await fetch(`${API_BASE}/users/me/photo-review`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...buildAuthHeaders(userId),
      },
      body: JSON.stringify({
        photo_url: photoUrl,
        photo_type: photoType,
        file_name: fileName,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      throw new Error(errorText || "Could not save photo review");
    }

    const data = await response.json().catch(() => ({}));
    return {
      data: data?.review || {
        id: `local-review-${userId || "unknown"}-${Date.now()}`,
        photo_url: photoUrl,
        photo_type: photoType,
        file_name: fileName,
        status: "pending",
      },
      error: null,
    };
  } catch (error) {
    console.error("Error creating photo review:", error);
    return {
      data: {
        id: `local-review-${userId || "unknown"}-${Date.now()}`,
        photo_url: photoUrl,
        photo_type: photoType,
        file_name: fileName,
        status: "pending",
        message:
          "Photo uploaded, but review queue sync failed. Ask admin to check the upload.",
      },
      error,
    };
  }
}

/**
 * Get photo review status for user
 */
export async function getPhotoReviewStatus(userId) {
  try {
    const API_BASE = getAPIRoot();
    const response = await fetch(`${API_BASE}/users/me/photo-review`, {
      credentials: "include",
      headers: buildAuthHeaders(userId),
    });
    if (!response.ok) {
      throw new Error("Unable to fetch photo review status");
    }
    const data = await response.json();
    const reviews = Array.isArray(data?.reviews) ? data.reviews : [];

    return { data: reviews, error: null };
  } catch (error) {
    console.error("Error fetching photo review status:", error);
    return { data: [], error };
  }
}

/**
 * Validate photo file before upload
 */
function validatePhotoFile(file) {
  const errors = [];

  // Check file type
  const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
  if (!allowedTypes.includes(file.type)) {
    errors.push("Please upload a JPEG, PNG, or WebP image");
  }

  // Check file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    errors.push("Image must be smaller than 5MB");
  }

  // Check minimum dimensions (optional)
  return new Promise((resolve) => {
    if (errors.length > 0) {
      resolve({ isValid: false, error: errors.join(", ") });
      return;
    }

    const img = new Image();
    img.onload = function () {
      // Check minimum dimensions
      if (this.width < 200 || this.height < 200) {
        errors.push("Image must be at least 200x200 pixels");
      }

      // Check aspect ratio for profile photos
      const aspectRatio = this.width / this.height;
      if (aspectRatio < 0.5 || aspectRatio > 2) {
        errors.push("Image aspect ratio should be between 1:2 and 2:1");
      }

      resolve({
        isValid: errors.length === 0,
        error: errors.length > 0 ? errors.join(", ") : null,
        dimensions: { width: this.width, height: this.height },
      });
    };

    img.onerror = function () {
      resolve({ isValid: false, error: "Invalid image file" });
    };

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Delete photo from storage
 */
export async function deletePhoto(filePath) {
  try {
    if (!filePath) throw new Error("Missing file path");
    return { data: true, error: null };
  } catch (error) {
    console.error("Error deleting photo:", error);
    return { data: null, error };
  }
}

/**
 * Get user's current profile photo
 */
export async function getCurrentProfilePhoto(userId) {
  try {
    const API_BASE = getAPIRoot();
    const response = await fetch(`${API_BASE}/users/me`, {
      credentials: "include",
      headers: buildAuthHeaders(userId),
    });
    if (!response.ok) {
      throw new Error("Unable to fetch current profile photo");
    }
    const data = await response.json();
    const avatarUrl = data?.avatar_url || data?.profile_image_url || null;

    return {
      data: {
        profileImageUrl: avatarUrl,
        avatarUrl,
        hasCustomPhoto: !!avatarUrl,
      },
      error: null,
    };
  } catch (error) {
    console.error("Error fetching current profile photo:", error);
    return { data: null, error };
  }
}

/**
 * Update user's profile photo URL after approval
 */
export async function updateProfilePhotoUrl(userId, photoUrl) {
  try {
    const API_BASE = getAPIRoot();
    const response = await fetch(`${API_BASE}/users/me`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...buildAuthHeaders(userId),
      },
      body: JSON.stringify({
        avatar_url: photoUrl,
        profile_image_url: photoUrl,
      }),
    });
    if (!response.ok) {
      throw new Error("Unable to save profile photo");
    }
    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error("Error updating profile photo URL:", error);
    return { data: null, error };
  }
}

/**
 * Get approved photos for user
 */
export async function getApprovedPhotos(userId) {
  try {
    await getPhotoReviewStatus(userId);
    return { data: [], error: null };
  } catch (error) {
    console.error("Error fetching approved photos:", error);
    return { data: [], error };
  }
}

// =============================================
// UTILITY FUNCTIONS
// =============================================

/**
 * Compress image before upload
 */
export function compressImage(file, maxWidth = 800, quality = 0.8) {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = function () {
      // Calculate new dimensions
      let { width, height } = this;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(this, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          resolve(
            new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            }),
          );
        },
        "image/jpeg",
        quality,
      );
    };

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Resize image so the longest side is maxDim. Preserves aspect ratio.
 */
export function resizeImageMax(
  file,
  maxDim = 1024,
  quality = 0.85,
  mimeType = "image/jpeg",
) {
  return new Promise((resolve) => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.onload = function () {
        let { width, height } = this;
        const longest = Math.max(width, height);
        if (longest > maxDim) {
          const scale = maxDim / longest;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(this, 0, 0, width, height);
        const type =
          mimeType ||
          (file.type && file.type.startsWith("image/")
            ? file.type
            : "image/jpeg");
        canvas.toBlob(
          (blob) => {
            if (!blob) return resolve(file);
            const ext = type.includes("webp")
              ? "webp"
              : type.includes("png")
                ? "png"
                : "jpg";
            const renamed = new File(
              [blob],
              (file.name || "photo").replace(/\.[^.]+$/, `.${ext}`),
              {
                type,
                lastModified: Date.now(),
              },
            );
            resolve(renamed);
          },
          type,
          quality,
        );
      };
      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    } catch (_) {
      resolve(file);
    }
  });
}

/**
 * Center-crop a profile image into a square so larger photos fit better by default.
 */
export function cropImageToSquare(
  file,
  size = 1024,
  quality = 0.86,
  mimeType = "image/jpeg",
) {
  return new Promise((resolve) => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.onload = function () {
        const sourceSize = Math.min(this.width, this.height);
        const sx = Math.round((this.width - sourceSize) / 2);
        const sy = Math.round((this.height - sourceSize) / 2);
        canvas.width = size;
        canvas.height = size;
        ctx.drawImage(this, sx, sy, sourceSize, sourceSize, 0, 0, size, size);
        const type =
          mimeType ||
          (file.type && file.type.startsWith("image/")
            ? file.type
            : "image/jpeg");
        canvas.toBlob(
          (blob) => {
            if (!blob) return resolve(file);
            const ext = type.includes("webp")
              ? "webp"
              : type.includes("png")
                ? "png"
                : "jpg";
            resolve(
              new File(
                [blob],
                (file.name || "photo").replace(/\.[^.]+$/, `.${ext}`),
                {
                  type,
                  lastModified: Date.now(),
                },
              ),
            );
          },
          type,
          quality,
        );
      };
      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    } catch (_) {
      resolve(file);
    }
  });
}
